import { FALLBACK_CONFIG } from './fallback-config';
import { pushGcmDefault, createApi, drainPending, SNIPPET_VERSION } from './api';
import { unblock } from './unblock';
import { readDecision, emptyConsent } from './storage';
import { pushGcmUpdate, pushDataLayerEvent } from './gcm';
import { fetchConfig } from './network';
import type { Config } from './types';

declare global {
  interface Window {
    cookiesie?: ReturnType<typeof createApi>;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

// mvp_b_plan.md §5.2 step 1: synchronous default BEFORE anything else.
// Google tags loaded after this still see denied defaults; the next async
// tick refines via gcmUpdate once a stored decision (or the user) lands.
pushGcmDefault();

(function bootstrap() {
  if (window.cookiesie) return; // double-load guard

  const script = (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-project-id]');

  const projectId = script?.dataset.projectId ?? '';
  const apiBase = (script?.dataset.apiBase ?? 'https://cookies.ie/api/v1').replace(/\/$/, '');
  const configUrl =
    script?.dataset.configUrl ??
    (projectId ? `${apiBase}/config/${projectId}.json` : '');

  const config: Config = {
    ...FALLBACK_CONFIG,
    apiBase,
    configUrl,
  };

  const state = {
    projectId,
    config,
    decision: readDecision(),
    ready: false,
    apiBase,
  };

  const api = createApi(state);
  window.cookiesie = api;

  const internal = api as unknown as {
    _fireReady: () => void;
    _bindFooter: () => void;
    _renderBanner: (startInPrefs?: boolean) => void;
  };

  // Step 3: provisionally apply the stored decision pre-fetch so existing
  // consent is honoured immediately (no flicker). The policy-revision
  // freshness check is deferred to step 5 — comparing against
  // FALLBACK_CONFIG.policyRevision here would wipe every stored decision
  // every page load because FALLBACK is baked at build-time.
  //
  // GCM + dataLayer updates fire synchronously: the snippet ships
  // `async` and is recommended for <head> placement, so it can execute
  // before any gtag.js loaded later on the page — we want our consent
  // signals to land first.
  //
  // unblock() is deferred until DOMContentLoaded if the parser is still
  // working. Otherwise `querySelectorAll('script[type=text/plain]…')` runs
  // before <body> is parsed and matches zero gated nodes, silently leaving
  // the customer's analytics/marketing scripts un-cloned on every return
  // visit — the recommended install puts the snippet in <head>, which is
  // precisely the case where this race fires.
  if (state.decision) {
    const stored = state.decision;
    pushGcmUpdate(stored.c);
    pushDataLayerEvent(stored.c);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => unblock(stored.c), { once: true });
    } else {
      unblock(stored.c);
    }
  }

  // Bind footer triggers on the host page.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', internal._bindFooter, {
      once: true,
    });
  } else {
    internal._bindFooter();
  }

  internal._fireReady();

  // Step 5: refresh from live config on next idle tick. This is where the
  // banner-vs-no-banner decision is finalised: stored decision still valid?
  // Show nothing. Stale (different policy_revision)? Re-prompt. No decision?
  // Either honour Sec-GPC or show the banner per the live config.
  //
  // `live` is null when the fetch failed AND no cached config exists in
  // localStorage. In that case we must NOT compare the stored decision's
  // policyRevision against FALLBACK_CONFIG.policyRevision (baked at build
  // time, always 1) — doing so wipes every stored decision whose project is
  // past revision 1 and re-prompts the user on every page load. Honour the
  // stored decision instead and don't render the banner; the fail-open
  // contract (§5.3) treats this as "keep applying the last known state".
  const decideAndMaybeRender = (live: Config | null): void => {
    const cfg = live ?? state.config;
    if (live && state.decision) {
      if (state.decision.pr === live.policyRevision) return; // already applied at step 3
      // Live config indicates a republish — discard the stored decision and
      // fall through to the GPC / banner branch below. Walk GCM signals
      // back to denied so GCM-aware tags stop sending events while the user
      // is being asked to re-consent against the new policy. (Cloned
      // <script> nodes from step 3 can't be un-cloned — their initial
      // requests have already fired; this is the unavoidable tradeoff of
      // pre-fetch no-flicker apply.)
      state.decision = null;
      pushGcmUpdate(emptyConsent());
    }
    if (!live && state.decision) {
      // Fail-open: keep applying the stored decision (already pushed at step
      // 3). No banner, no wipe. The next page-load will retry the fetch.
      return;
    }
    const gpcOn = navigator.globalPrivacyControl === true;
    if (gpcOn && cfg.defaults.honourGpc) {
      const c = emptyConsent();
      pushGcmUpdate(c);
      api.rejectAll();
      return;
    }
    runWhen(cfg.defaults.showBannerAfter, () => {
      if (!state.decision) internal._renderBanner();
    });
  };

  if (configUrl) {
    afterFirstPaint(async () => {
      const live = await fetchConfig(configUrl);
      if (live) {
        state.config = { ...live, apiBase, configUrl };
      }
      decideAndMaybeRender(live);
      // Drain any queued events that failed earlier — banner_text_hash now
      // resolves against the live config so the server will accept them.
      drainPending(state).catch(() => {});
    });
  } else {
    // No configUrl (raw snippet test mode / no data-project-id) — decide
    // against the fallback synchronously.
    decideAndMaybeRender(null);
  }
})();

type WhenSpec =
  | 'load'
  | 'interactive'
  | 'idle'
  | { delayMs: number };

function runWhen(spec: WhenSpec, fn: () => void): void {
  if (typeof spec === 'object' && spec) {
    setTimeout(fn, Math.max(0, spec.delayMs));
    return;
  }
  if (spec === 'load') {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', fn, { once: true });
    return;
  }
  if (spec === 'idle') {
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void) => void)
      | undefined;
    if (ric) ric(fn);
    else setTimeout(fn, 200);
    return;
  }
  // 'interactive' (default)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

function afterFirstPaint(fn: () => void): void {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void) => void)
    | undefined;
  if (ric) ric(fn);
  else setTimeout(fn, 0);
}

export { SNIPPET_VERSION };
